<script setup lang="ts">
    import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
    import { useDisplay, useTheme } from 'vuetify';
    import { useEventStore } from '@/stores/event';
    import { EventPeriod } from '@/constants';
    import EventMarket1X2 from '@/pages/core/event/EventMarket1X2.vue';
    import EventStats from '@/pages/core/event/EventStats.vue';
    import EventMarketDC from '@/pages/core/event/EventMarketDC.vue';
    import EventMarketGoal from '@/pages/core/event/EventMarketGoal.vue';
    import EventMarketHDP from '@/pages/core/event/EventMarketHDP.vue';
    import EventMarketBTS from '@/pages/core/event/EventMarketBTS.vue';
    import EventMarketCS from '@/pages/core/event/EventMarketCS.vue';
    import EventMarketCornersGoal from '@/pages/core/event/EventMarketCornersGoal.vue';
    import EventMarketCornersHDP from '@/pages/core/event/EventMarketCornersHDP.vue';

    const { tableHeight = undefined } = defineProps<{
        tableHeight?: number;
    }>();

    const theme = useTheme();
    const display = useDisplay();
    const { t, locale } = useI18n();

    // function setBetDialogRef(el: any, id: number) {
    //     const key = 'betDialog' + id;
    //     if (el) betDialogsRefs[key] = el;
    // }
    // provide('betDialogsRefs', betDialogsRefs);

    const eventStore = useEventStore();

    const isCornersMarketsOnly = ref(false);
    const periodId = ref<number | null>(null); // ALL Periods
    const marketRefs = reactive<any[]>([]);
    const isExpanded = ref(true);
    const toggleFilters = ref(0);

    function showMarkets(eventPeriodId: EventPeriod | null, isCorners = false) {
        isCornersMarketsOnly.value = isCorners;
        periodId.value = eventPeriodId;
    }

    function toggleAllMarkets() {
        isExpanded.value = !isExpanded.value;
        marketRefs.forEach((marketRef: any) => (marketRef.isExpanded = isExpanded.value));
    }

    function setMarketRef(el: any) {
        if (el) {
            const existing = marketRefs.find((m: any) => m?.name === el.name && m.periodId === el.periodId);
            if (!existing) {
                marketRefs.push(el);
            }
        }
    }

    const firstVisibleMarketName = computed(() => {
        const m: any = marketRefs.find((market: any) => market.isShow === true);
        return m ? m.name + '_' + m.periodId : '';
    });

    const buttonsWidth = computed(() => {
        let width = display.width.value;
        if (width > 1315) {
            width = 600;
        } else if (width <= 1315 && width > 940) {
            width = width - width * 0.33;
        }
        if (width > 600) {
            width = width - width * 0.15;
        }
        return (width - 16 - 16) / 4;
    });

    onMounted(() => {
        console.log('Component %s mounted.', getCurrentInstance()?.type.__name);
    });

    onBeforeUnmount(() => {
        console.log('Component %s unmounted.', getCurrentInstance()?.type.__name);
    });
</script>

<template>
    <!--    <v-container>-->

    <!--        <v-row>-->
    <v-card tile elevation="0">
        <v-card-title :style="theme.name.value === 'dark' ? 'background-color: #1e1f22' : 'background-color: white!important'" class="pa-4 text-center">
            <v-col cols="12" class="pa-0">
                <v-btn-toggle tag="a" v-model="toggleFilters" mandatory="force" divided density="compact" class="period-switcher" variant="outlined" color="tbGreen">
                    <v-btn tag="a" :width="buttonsWidth" class="period-button" :class="locale === 'pt' ? 'pt' : ''" base-color="tbGreen" @click="showMarkets(null, false)">
                        {{ t('event.allMarkets') }}
                    </v-btn>

                    <v-btn tag="a" :width="buttonsWidth" class="period-button" :class="locale === 'pt' ? 'pt' : ''" base-color="tbGreen" @click="showMarkets(EventPeriod.FullTime, false)">
                        {{ t('event.marketFT') }}
                    </v-btn>

                    <v-btn tag="a" :width="buttonsWidth" class="period-button" :class="locale === 'pt' ? 'pt' : ''" base-color="tbGreen" @click="showMarkets(EventPeriod.FirstHalf, false)">
                        {{ t('event.market1H') }}
                    </v-btn>

                    <v-btn tag="a" :width="buttonsWidth" class="period-button" :class="locale === 'pt' ? 'pt' : ''" base-color="tbGreen" @click="showMarkets(null, true)">
                        {{ t('event.corners') }}
                    </v-btn>
                </v-btn-toggle>
            </v-col>
        </v-card-title>
        <v-card-text class="pa-0" style="overflow-y: auto" :style="'height: ' + (Number(tableHeight) - 172) + 'px'">
            <!--                FT-->
            <EventStats v-show="eventStore.isLiveStatsExist() && display.width.value <= 940" :ref="setMarketRef"></EventStats>
            <EventMarket1X2
                :ref="setMarketRef"
                :isShowAllMarketsToggle="firstVisibleMarketName === 'app-eventMarket1X2_1'"
                :isAllExpanded="isExpanded"
                :isShow="eventStore.market1x2FT !== null && (periodId === EventPeriod.FullTime || periodId === null) && isCornersMarketsOnly === false"
                v-show="eventStore.market1x2FT !== null && (periodId === EventPeriod.FullTime || periodId === null) && isCornersMarketsOnly === false"
                @toggle-all-markets="toggleAllMarkets"
            ></EventMarket1X2>
            <EventMarketDC
                :ref="setMarketRef"
                :isShowAllMarketsToggle="firstVisibleMarketName === 'app-eventMarketDC_1'"
                :isAllExpanded="isExpanded"
                :isShow="eventStore.marketDcFT !== null && (periodId === EventPeriod.FullTime || periodId === null) && isCornersMarketsOnly === false"
                v-show="eventStore.marketDcFT !== null && (periodId === EventPeriod.FullTime || periodId === null) && isCornersMarketsOnly === false"
                @toggle-all-markets="toggleAllMarkets"
            ></EventMarketDC>
            <EventMarketGoal
                :ref="setMarketRef"
                :isShowAllMarketsToggle="firstVisibleMarketName === 'app-eventMarketOU_1'"
                :isAllExpanded="isExpanded"
                :isShow="eventStore.marketGoalFT !== null && (periodId === EventPeriod.FullTime || periodId === null) && isCornersMarketsOnly === false"
                v-show="eventStore.marketGoalFT !== null && (periodId === EventPeriod.FullTime || periodId === null) && isCornersMarketsOnly === false"
                @toggle-all-markets="toggleAllMarkets"
            ></EventMarketGoal>
            <EventMarketHDP
                :ref="setMarketRef"
                :isShowAllMarketsToggle="firstVisibleMarketName === 'app-eventMarketHDP_1'"
                :isAllExpanded="isExpanded"
                :isShow="eventStore.marketHandicapFT !== null && (periodId === EventPeriod.FullTime || periodId === null) && isCornersMarketsOnly === false"
                v-show="eventStore.marketHandicapFT !== null && (periodId === EventPeriod.FullTime || periodId === null) && isCornersMarketsOnly === false"
                @toggle-all-markets="toggleAllMarkets"
            ></EventMarketHDP>
            <EventMarketBTS
                :ref="setMarketRef"
                :isShowAllMarketsToggle="firstVisibleMarketName === 'app-eventMarketBTS_1'"
                :isAllExpanded="isExpanded"
                :isShow="eventStore.marketBtsFT !== null && (periodId === EventPeriod.FullTime || periodId === null) && isCornersMarketsOnly === false"
                v-show="eventStore.marketBtsFT !== null && (periodId === EventPeriod.FullTime || periodId === null) && isCornersMarketsOnly === false"
                @toggle-all-markets="toggleAllMarkets"
            ></EventMarketBTS>
            <EventMarketCS
                :ref="setMarketRef"
                :isShowAllMarketsToggle="firstVisibleMarketName === 'app-eventMarketCS_1'"
                :isAllExpanded="isExpanded"
                :isShow="eventStore.marketCsFT !== null && (periodId === EventPeriod.FullTime || periodId === null) && isCornersMarketsOnly === false"
                v-show="eventStore.marketCsFT !== null && (periodId === EventPeriod.FullTime || periodId === null) && isCornersMarketsOnly === false"
                @toggle-all-markets="toggleAllMarkets"
            ></EventMarketCS>
            <EventMarketCornersGoal
                :ref="setMarketRef"
                :isShowAllMarketsToggle="firstVisibleMarketName === 'app-eventMarketCornersOU_1'"
                :isAllExpanded="isExpanded"
                :isShow="eventStore.marketCornersGoalFT !== null && (periodId === EventPeriod.FullTime || periodId === null || isCornersMarketsOnly === true)"
                v-show="eventStore.marketCornersGoalFT !== null && (periodId === EventPeriod.FullTime || periodId === null || isCornersMarketsOnly === true)"
                @toggle-all-markets="toggleAllMarkets"
            ></EventMarketCornersGoal>
            <EventMarketCornersHDP
                :ref="setMarketRef"
                :isShowAllMarketsToggle="firstVisibleMarketName === 'app-eventMarketCornersHDP_1'"
                :isAllExpanded="isExpanded"
                :isShow="eventStore.marketCornersHandicapFT !== null && (periodId === EventPeriod.FullTime || periodId === null || isCornersMarketsOnly === true)"
                v-show="eventStore.marketCornersHandicapFT !== null && (periodId === EventPeriod.FullTime || periodId === null || isCornersMarketsOnly === true)"
                @toggle-all-markets="toggleAllMarkets"
            ></EventMarketCornersHDP>
            <!--1H-->
            <EventMarket1X2
                :ref="setMarketRef"
                :isShowAllMarketsToggle="firstVisibleMarketName === 'app-eventMarket1X2_2'"
                :isAllExpanded="isExpanded"
                :isShow="eventStore.market1x21H !== null && (periodId === EventPeriod.FirstHalf || periodId === null) && isCornersMarketsOnly === false"
                :period-id="EventPeriod.FirstHalf"
                v-show="eventStore.market1x21H !== null && (periodId === EventPeriod.FirstHalf || periodId === null) && isCornersMarketsOnly === false"
                @toggle-all-markets="toggleAllMarkets"
            ></EventMarket1X2>
            <EventMarketGoal
                :ref="setMarketRef"
                :isShowAllMarketsToggle="firstVisibleMarketName === 'app-eventMarketOU_2'"
                :isAllExpanded="isExpanded"
                :isShow="eventStore.marketGoal1H !== null && (periodId === EventPeriod.FirstHalf || periodId === null) && isCornersMarketsOnly === false"
                :period-id="EventPeriod.FirstHalf"
                v-show="eventStore.marketGoal1H !== null && (periodId === EventPeriod.FirstHalf || periodId === null) && isCornersMarketsOnly === false"
                @toggle-all-markets="toggleAllMarkets"
            ></EventMarketGoal>
            <EventMarketHDP
                :ref="setMarketRef"
                :isShowAllMarketsToggle="firstVisibleMarketName === 'app-eventMarketHDP_2'"
                :isAllExpanded="isExpanded"
                :isShow="eventStore.marketHandicap1H !== null && (periodId === EventPeriod.FirstHalf || periodId === null) && isCornersMarketsOnly === false"
                :period-id="EventPeriod.FirstHalf"
                v-show="eventStore.marketHandicap1H !== null && (periodId === EventPeriod.FirstHalf || periodId === null) && isCornersMarketsOnly === false"
                @toggle-all-markets="toggleAllMarkets"
            ></EventMarketHDP>
            <EventMarketBTS
                :ref="setMarketRef"
                :isShowAllMarketsToggle="firstVisibleMarketName === 'app-eventMarketBTS_2'"
                :isAllExpanded="isExpanded"
                :isShow="eventStore.marketBts1H !== null && (periodId === EventPeriod.FirstHalf || periodId === null) && isCornersMarketsOnly === false"
                :period-id="EventPeriod.FirstHalf"
                v-show="eventStore.marketBts1H !== null && (periodId === EventPeriod.FirstHalf || periodId === null) && isCornersMarketsOnly === false"
                @toggle-all-markets="toggleAllMarkets"
            ></EventMarketBTS>
            <EventMarketCS
                :ref="setMarketRef"
                :period-id="EventPeriod.FirstHalf"
                :isShowAllMarketsToggle="firstVisibleMarketName === 'app-eventMarketCS_2'"
                :isAllExpanded="isExpanded"
                :isShow="eventStore.marketCs1H !== null && (periodId === EventPeriod.FirstHalf || periodId === null) && isCornersMarketsOnly === false"
                v-show="eventStore.marketCs1H !== null && (periodId === EventPeriod.FirstHalf || periodId === null) && isCornersMarketsOnly === false"
                @toggle-all-markets="toggleAllMarkets"
            ></EventMarketCS>
            <EventMarketCornersGoal
                :ref="setMarketRef"
                :period-id="EventPeriod.FirstHalf"
                :isShowAllMarketsToggle="firstVisibleMarketName === 'app-eventMarketCornersOU_2'"
                :isAllExpanded="isExpanded"
                :isShow="eventStore.marketCornersGoal1H !== null && (periodId === EventPeriod.FirstHalf || periodId === null || isCornersMarketsOnly === true)"
                v-show="eventStore.marketCornersGoal1H !== null && (periodId === EventPeriod.FirstHalf || periodId === null || isCornersMarketsOnly === true)"
                @toggle-all-markets="toggleAllMarkets"
            ></EventMarketCornersGoal>
            <EventMarketCornersHDP
                :ref="setMarketRef"
                :period-id="EventPeriod.FirstHalf"
                :isShowAllMarketsToggle="firstVisibleMarketName === 'app-eventMarketCornersHDP_2'"
                :isAllExpanded="isExpanded"
                :isShow="eventStore.marketCornersHandicap1H !== null && (periodId === EventPeriod.FirstHalf || periodId === null || isCornersMarketsOnly === true)"
                v-show="eventStore.marketCornersHandicap1H !== null && (periodId === EventPeriod.FirstHalf || periodId === null || isCornersMarketsOnly === true)"
                @toggle-all-markets="toggleAllMarkets"
            ></EventMarketCornersHDP>
        </v-card-text>
        <!--        <BetDialog-->
        <!--            v-for="index in 5"-->
        <!--            :ref="(el) => setBetDialogRef(el, index)"-->
        <!--            :width="440"-->
        <!--            :id="index + ''"-->
        <!--            :key="index"-->
        <!--            module="Event"-->
        <!--            @cancel="cancel"-->
        <!--            :contentClass="'event-bet-dialog'"-->
        <!--            :idPrefix="'event-bet-dialog-'"-->
        <!--        ></BetDialog>-->
    </v-card>
    <!--        </v-row>-->
    <!--    </v-container>-->
</template>

<style scoped>
    .period-switcher.v-btn-toggle:not(.v-btn-toggle--group) .v-btn.v-btn {
        border-color: #6aa84f !important;
    }
    .period-button {
        /*border: 1px solid #6aa84f !important;*/
        /*color: #6aa84f !important;*/

        font-size: 13px !important;
        letter-spacing: 0 !important;
        /*text-transform: none!important;*/
    }
    .v-theme--dark .period-button {
        background-color: #323338;
    }

    @media only screen and (max-width: 1100px) {
        .period-button.pt {
            font-size: 12px !important;
        }
    }

    @media only screen and (max-width: 680px) {
        .period-button.pt {
            font-size: 9px !important;
        }
    }

    @media only screen and (max-width: 600px) {
        .period-button {
            font-size: 10px !important;
        }

        .period-button.pt {
            font-size: 7px !important;
        }
    }

    @media only screen and (max-width: 320px) {
        .period-button {
            font-size: 8px !important;
        }
    }
</style>
